import React from 'react';
import { useDevice } from './DeviceContext';
import DeviceSwitcher from './DeviceSwitcher';
import { Lock, ArrowLeft, ArrowRight, RotateCcw, MoreHorizontal } from 'lucide-react';

/**
 * Device preview chrome for the employee survey prototype.
 *
 * Wraps its children (the full employee app) in a realistic-looking device
 * shell so reviewers can see how it lays out on mobile, tablet, and desktop
 * in the same browser window. Includes the {@link DeviceSwitcher} overlay so
 * reviewers can toggle form factors without leaving the page.
 *
 * The chrome is purely decorative; the inner app itself is device-aware via
 * {@link EmployeeFrame} / {@link EmployeeDesktopFrame}.
 */

const PhoneChrome = ({ children }) => (
  <div className='min-h-screen flex flex-col items-center justify-start py-6 lg:py-10 px-4'>
    <div className='relative w-[418px] rounded-[44px] bg-[#1a1a1a] p-[14px] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.35)]'>
      <div className='absolute top-[22px] left-1/2 -translate-x-1/2 w-[110px] h-[28px] bg-[#1a1a1a] rounded-full z-20 flex items-center justify-end pr-3 gap-1'>
        <span className='w-1.5 h-1.5 rounded-full bg-[#333]' />
        <span className='w-3 h-3 rounded-full bg-[#222] border border-[#333]' />
      </div>
      <div className='relative w-full rounded-[32px] bg-background overflow-hidden' style={{ height: 'min(844px, calc(100vh - 120px))' }}>
        <div className='absolute top-0 left-0 right-0 h-[44px] flex items-center justify-between px-8 pt-2 text-[12px] font-semibold z-10 text-foreground pointer-events-none'>
          <span>9:41</span>
          <span className='flex items-center gap-1'>
            <span className='tracking-[0.05em]'>●●●●●</span>
            <span className='ml-1'>5G</span>
            <span className='ml-1 inline-block w-6 h-3 border border-foreground/70 rounded-sm relative'>
              <span className='absolute left-0.5 top-0.5 bottom-0.5 right-0.5 bg-foreground rounded-[1px]' />
            </span>
          </span>
        </div>
        <div className='w-full h-full overflow-y-auto pt-[44px]'>{children}</div>
        <div className='absolute bottom-2 left-1/2 -translate-x-1/2 w-[134px] h-[5px] rounded-full bg-foreground/80 pointer-events-none' />
      </div>
    </div>
    <DevicePreviewCaption label='iPhone 14 Pro · 390 × 844' />
  </div>
);

const TabletChrome = ({ children }) => (
  <div className='min-h-screen flex flex-col items-center justify-start py-6 lg:py-10 px-4 overflow-x-auto'>
    <div className='relative w-[804px] flex-shrink-0 rounded-[38px] bg-[#1a1a1a] p-[18px] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.35)]'>
      <div className='absolute top-1/2 -translate-y-1/2 left-[6px] w-[6px] h-[6px] rounded-full bg-[#2a2a2a]' />
      <div
        className='relative w-full rounded-[24px] bg-background overflow-hidden'
        style={{ height: 'min(1024px, calc(100vh - 160px))' }}>
        <div className='w-full h-full overflow-y-auto'>{children}</div>
      </div>
    </div>
    <DevicePreviewCaption label='iPad · 768 × 1024' />
  </div>
);

const BrowserChrome = ({ children }) => (
  <div className='min-h-screen flex flex-col'>
    <div className='h-10 border-b border-border bg-muted/60 flex items-center gap-3 px-4 flex-shrink-0'>
      <div className='flex items-center gap-1.5'>
        <span className='w-3 h-3 rounded-full bg-[#ff5f57]' />
        <span className='w-3 h-3 rounded-full bg-[#febc2e]' />
        <span className='w-3 h-3 rounded-full bg-[#28c840]' />
      </div>
      <div className='flex items-center gap-1 text-muted-foreground'>
        <ArrowLeft size={14} strokeWidth={1.5} className='opacity-40' />
        <ArrowRight size={14} strokeWidth={1.5} className='opacity-40' />
        <RotateCcw size={12} strokeWidth={1.5} />
      </div>
      <div className='flex-1 flex items-center gap-2 bg-background border border-border/70 rounded-md px-3 py-1 text-[12px] text-muted-foreground max-w-[520px]'>
        <Lock size={11} strokeWidth={1.75} className='text-success' />
        <span className='truncate'>survey.cyf.app/s/abc123xyz</span>
      </div>
      <MoreHorizontal size={14} strokeWidth={1.5} className='text-muted-foreground opacity-60' />
    </div>
    <div className='flex-1 bg-background'>{children}</div>
  </div>
);

const DevicePreviewCaption = ({ label }) => (
  <div className='mt-3 font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground'>
    {label}
  </div>
);

const DevicePreview = ({ children }) => {
  const { device } = useDevice();
  const Chrome = device === 'tablet' ? TabletChrome : device === 'desktop' ? BrowserChrome : PhoneChrome;
  return (
    <>
      <DeviceSwitcher />
      <Chrome>{children}</Chrome>
    </>
  );
};

export default DevicePreview;
